import { useCallback, useEffect, useState } from "react";

// Anonymous-trial counter. We persist the count across browser sessions
// so closing/reopening doesn't reset the limit (Item #7).
//
// Belt-and-braces persistence:
//   • Primary:  localStorage   (survives close, syncs across tabs)
//   • Backup:   IndexedDB-free fallback via two keys (raw + obfuscated)
//   • Cookie:   Set as a long-lived cookie so private-mode clears localStorage
//               but cookies sometimes survive on the same device
//
// We take the MAX of all three signals when reading — so clearing one but
// not all three still preserves the count.

const LS_KEY = "replyai.anonGenerations";
const LS_KEY_ALT = "macha.tg.v1";          // obfuscated mirror so accidental "clear ReplyAI keys" misses it
const COOKIE_KEY = "macha_tg";
const DEFAULT_LIMIT = 3;
const COOKIE_DAYS = 365;

function readCookie(name) {
  try {
    const parts = document.cookie.split(";").map((s) => s.trim());
    for (const p of parts) {
      if (p.startsWith(name + "=")) return decodeURIComponent(p.slice(name.length + 1));
    }
  } catch { /* ignore */ }
  return null;
}

function writeCookie(name, value, days = COOKIE_DAYS) {
  try {
    const exp = new Date(Date.now() + days * 86400_000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${exp}; path=/; SameSite=Lax`;
  } catch { /* ignore */ }
}

function readPersistedCount() {
  let max = 0;
  try {
    const v = parseInt(localStorage.getItem(LS_KEY) || "0", 10);
    if (Number.isFinite(v) && v > max) max = v;
  } catch { /* ignore */ }
  try {
    const v = parseInt(localStorage.getItem(LS_KEY_ALT) || "0", 10);
    if (Number.isFinite(v) && v > max) max = v;
  } catch { /* ignore */ }
  const ck = readCookie(COOKIE_KEY);
  if (ck) {
    const v = parseInt(ck, 10);
    if (Number.isFinite(v) && v > max) max = v;
  }
  return max;
}

function writePersistedCount(n) {
  const s = String(n);
  try { localStorage.setItem(LS_KEY, s); } catch { /* ignore */ }
  try { localStorage.setItem(LS_KEY_ALT, s); } catch { /* ignore */ }
  writeCookie(COOKIE_KEY, s);
}

/**
 * Anonymous-trial counter for the AI Reply Assistant.
 * Survives browser close + cleared localStorage (multi-key + cookie fallback).
 */
export function useFreeTrial(limit = DEFAULT_LIMIT) {
  const [used, setUsed] = useState(0);

  useEffect(() => {
    setUsed(readPersistedCount());
  }, []);

  const increment = useCallback(() => {
    setUsed((prev) => {
      const next = prev + 1;
      writePersistedCount(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(LS_KEY_ALT); } catch { /* ignore */ }
    writeCookie(COOKIE_KEY, "0", -1); // expire
    setUsed(0);
  }, []);

  const remaining = Math.max(0, limit - used);
  const exhausted = used >= limit;

  return { used, remaining, exhausted, limit, increment, reset };
}

const DRAFT_KEY = "replyai.replyAssistant.draft";

/** Preserves the current Reply Assistant draft so signup doesn't lose it. */
export const draftStore = {
  save(draft) {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch { /* ignore */ }
  },
  load() {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },
  clear() {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
  },
};
